import { useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  PageContainer,
  Tabs,
  Button,
  DataTable,
  Skeleton,
  ErrorBoundary,
  ExpiryAlertBadge,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { productService } from '@/services/product.service';
import { useDashboardStore } from '@/stores/use-dashboard-store';
import type { ExpiryAlertItem, ExpiryAlertsResponse } from '@/types/product';

type ExpiryRow = ExpiryAlertItem & { id: string };

// ─── CSV Escaping ────────────────────────────────────────────────────────────

function escapeCsvField(value: string | number | undefined): string {
  const str = String(value ?? '');
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

// Page size used when paging through the full result set for a CSV export.
// The on-screen table is fixed at 20 rows/page; the export must cover the whole
// window, so we pull larger pages to keep the number of round-trips small.
const EXPORT_PAGE_SIZE = 500;

/**
 * Build the expiry-alerts CSV (header + one row per batch) from a full row set.
 * See GitHub issue #232 — the export previously used only the current on-screen
 * page (≤20 rows) despite a filename implying the complete per-tab window.
 */
export function buildExpiryCsv(rows: ExpiryAlertItem[]): string {
  const header = 'Product,SKU,Batch,Expiry Date,Days Remaining,Stock,Unit,Location\n';
  const body = rows
    .map((r) =>
      [
        escapeCsvField(r.product_name),
        escapeCsvField(r.sku),
        escapeCsvField(r.batch_number),
        r.expiry_date,
        r.days_remaining,
        r.stock_quantity,
        escapeCsvField(r.unit),
        escapeCsvField(r.location_name),
      ].join(','),
    )
    .join('\n');
  return header + body;
}

/**
 * Page through every batch in the current tab/window so the CSV export covers the
 * complete dataset (all N rows the table reports as `meta.total`), not just the
 * page currently on screen. See GitHub issue #232.
 */
export async function fetchAllExpiryAlerts(
  fetchPage: (page: number, limit: number) => Promise<ExpiryAlertsResponse>,
): Promise<ExpiryAlertItem[]> {
  const first = await fetchPage(1, EXPORT_PAGE_SIZE);
  const rows = [...first.data];
  const totalPages = first.meta?.totalPages ?? 1;
  for (let p = 2; p <= totalPages; p += 1) {
    const next = await fetchPage(p, EXPORT_PAGE_SIZE);
    rows.push(...next.data);
  }
  return rows;
}

const EXPIRY_TABS = [
  { id: 'expired', label: 'Expired' },
  { id: '7', label: '7 Days' },
  { id: '15', label: '15 Days' },
  { id: '30', label: '30 Days' },
];

function getWindowDays(tab: string): number | undefined {
  if (tab === '7') return 7;
  if (tab === '15') return 15;
  if (tab === '30') return 30;
  return 0; // expired = 0 days
}

// ─── Row action destinations ─────────────────────────────────────────────────

interface ExpiryProductRef {
  product_id: string;
  product_name: string;
}

/**
 * Build the URL that opens the Wastage "Record Wastage" flow, pre-filled with the
 * expiring product and an EXPIRED wastage type so the manager can write the stock
 * off as a loss in one click. See GitHub issue #231 — the row "Write Off" action
 * previously dead-ended on a "coming soon" toast even though the Wastage module
 * exists at /inventory/wastage.
 */
export function buildWriteOffUrl(product: ExpiryProductRef): string {
  const params = new URLSearchParams({
    product_id: product.product_id,
    product_name: product.product_name,
    wastage_type: 'EXPIRED',
  });
  return `/inventory/wastage?${params.toString()}`;
}

/**
 * Build the URL that opens the Discount Rules "New Discount Rule" flow, pre-filled
 * with the near-expiry product on the Item-Level tab so the manager can create a
 * clearance markdown to sell it before it expires. See GitHub issue #231 — the row
 * "Discount" action previously dead-ended on a "coming soon" toast even though the
 * Discounts module exists at /discounts.
 */
export function buildDiscountUrl(product: ExpiryProductRef): string {
  const params = new URLSearchParams({
    tab: 'ITEM_LEVEL',
    product_id: product.product_id,
    product_name: product.product_name,
  });
  return `/discounts?${params.toString()}`;
}

// ─── Table Columns ───────────────────────────────────────────────────────────

const columns: ColumnDef<ExpiryRow>[] = [
  {
    key: 'product_name',
    header: 'Product',
    render: (_v, row) => (
      <div>
        <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{row.product_name}</p>
        <p className="text-xs text-neutral-400 dark:text-neutral-500">{row.sku}</p>
      </div>
    ),
  },
  {
    key: 'batch_number',
    header: 'Batch #',
    render: (_v, row) => (
      <span className="text-sm text-neutral-600 dark:text-neutral-300">{row.batch_number ?? '\u2014'}</span>
    ),
  },
  {
    key: 'expiry_date',
    header: 'Expiry Date',
    render: (_v, row) => (
      <span className="text-sm text-neutral-700 dark:text-neutral-300 tabular-nums">
        {new Date(row.expiry_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
      </span>
    ),
  },
  {
    key: 'days_remaining',
    header: 'Urgency',
    render: (_v, row) => (
      // Drive the badge off the server's days_remaining so it agrees with the API
      // severity and the exported CSV, instead of recomputing in the viewer's
      // local timezone (which drifts by a day near a boundary). See GitHub #233.
      <ExpiryAlertBadge expiryDate={new Date(row.expiry_date)} daysRemaining={row.days_remaining} />
    ),
  },
  {
    key: 'stock_quantity',
    header: 'Stock',
    numeric: true,
    render: (_v, row) => (
      <span className="text-sm text-neutral-700 dark:text-neutral-300 tabular-nums">
        {Number(row.stock_quantity).toLocaleString('en-IN')} {row.unit}
      </span>
    ),
  },
  {
    key: 'location_name',
    header: 'Location',
    render: (_v, row) => (
      <span className="text-sm text-neutral-500 dark:text-neutral-400">{row.location_name ?? '\u2014'}</span>
    ),
  },
];

function ExpiryAlertsContent() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const selectedLocationId = useDashboardStore((s) => s.selectedLocationId);

  const activeTab = searchParams.get('tab') ?? '7';
  const page = Number(searchParams.get('page') ?? '1');
  const windowDays = getWindowDays(activeTab);

  const { data, isLoading, error } = useQuery({
    queryKey: ['expiry-alerts', selectedLocationId, windowDays, page],
    queryFn: () =>
      productService.getExpiryAlerts({
        location_id: selectedLocationId,
        window_days: windowDays,
        page,
        limit: 20,
      }),
    staleTime: 30_000,
  });

  const handleExportCsv = useCallback(async () => {
    if (!data?.data?.length) return;
    let rows: ExpiryAlertItem[];
    try {
      rows = await fetchAllExpiryAlerts((p, limit) =>
        productService.getExpiryAlerts({
          location_id: selectedLocationId,
          window_days: windowDays,
          page: p,
          limit,
        }),
      );
    } catch {
      toast({ title: 'Export failed', description: 'Could not load all expiry alerts. Please try again.', variant: 'error' });
      return;
    }
    if (!rows.length) return;
    const blob = new Blob([buildExpiryCsv(rows)], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `expiry-alerts-${activeTab}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [data, activeTab, selectedLocationId, windowDays, toast]);

  function handleTabChange(tabId: string) {
    const params = new URLSearchParams(searchParams);
    params.set('tab', tabId);
    params.set('page', '1');
    setSearchParams(params);
  }

  const expiryRows: ExpiryRow[] = (data?.data ?? []).map((item) => ({ ...item, id: item.id }));

  if (error) {
    return (
      <div className="rounded-xl bg-danger-50 dark:bg-danger-900/20 shadow-md p-4 text-sm text-danger-700 dark:text-danger-300">
        Failed to load expiry alerts. Please try refreshing.
      </div>
    );
  }

  return (
    <>
      {/* Gradient header */}
      <div className="bg-gradient-to-r from-danger-600 to-warning-600 rounded-xl p-6 text-white shadow-lg mb-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Expiry Alerts</h1>
            <p className="text-danger-100 mt-1">Products nearing or past their expiry date</p>
          </div>
          <Button
            variant="secondary"
            onClick={handleExportCsv}
            disabled={!data?.data?.length}
            className="bg-white/20 hover:bg-white/30 text-white border-white/30"
          >
            Export CSV
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 animate-slide-up">
        <Tabs
          items={EXPIRY_TABS}
          activeId={activeTab}
          onChange={handleTabChange}
          scrollable
        />
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={56} />
          ))}
        </div>
      ) : (
        <div className="animate-slide-up">
          <DataTable<ExpiryRow>
            columns={[
              ...columns,
              {
                key: 'actions',
                header: 'Action',
                render: (_v, row) => (
                  <div className="flex gap-1">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(buildWriteOffUrl(row));
                      }}
                    >
                      Write Off
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(buildDiscountUrl(row));
                      }}
                    >
                      Discount
                    </Button>
                  </div>
                ),
              },
            ]}
            data={expiryRows}
            loading={isLoading}
            totalCount={data?.meta?.total}
            currentPage={page}
            pageSize={20}
            onPageChange={(p) => {
              const params = new URLSearchParams(searchParams);
              params.set('page', String(p));
              setSearchParams(params);
            }}
            emptyState={{
              icon: (
                <svg className="h-12 w-12 text-success-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ),
              title: 'No expiry alerts',
              description: 'No products are nearing their expiry date',
            }}
          />
        </div>
      )}
    </>
  );
}

export function ExpiryAlertsPage() {
  return (
    <PageContainer
      title="Expiry Alerts"
      subtitle="Products nearing or past their expiry date"
      breadcrumbs={[{ label: 'Inventory', href: '/inventory' }, { label: 'Expiry Alerts' }]}
    >
      <ErrorBoundary resetKey="expiry-alerts">
        <ExpiryAlertsContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
